import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'
import {
  CHECKOUT_RULE_DOCUMENT_TYPES,
  type CheckoutRuleDocumentType,
} from '../../interfaces'
import { useGetCheckoutRuleQuery } from '../../graphQL/queries/getCheckoutRule'
import type { ICheckoutRuleValue } from '../../graphQL/queries/getCheckoutRule/types'
import {
  markGuidedWalkStarted,
  selectAutoSettleStatus,
  setAutoSettleStatus,
  setAvailable,
  setGuidedReturn,
  setLoadingState,
} from '../../redux/slices/checkout'
import type { AutoSettleStatus } from '../../redux/slices/checkout/types'
import type { AppStore, RootState } from '../../redux/store'
import {
  ESCAPES_ORIGIN,
  resolveCheckoutRuleRoute,
} from '../../utils/checkoutRuleRoute'
import { selectFirstHeldRule } from '../../utils/checkoutRuleSelection'
import { generateDataDogContext } from '../../utils/dataDog'
import { fireCheckoutRuleResolved } from '../../utils/fireCheckoutRuleResolved'
import useAppDispatch from '../useAppDispatch'
import useAppStore from '../useAppStore'
import usePushView from '../usePushView'
import {
  AUTO_SETTLE_DEADLINE_MS,
  decideAutoSettle,
} from './useCheckoutRuleAutoSettle'
import type { CheckoutRuleSelect } from './useCheckoutRuleSelection'

const FUNCTION_NAME = 'startGuidedWalk'

/** What `templateActions.checkout.startGuidedWalk`'s authored `args` carry. */
export interface StartGuidedWalkArgs {
  /**
   * One of the storefront's own `routes[].route` patterns, spelled exactly as
   * authored there — `'/:slug/:sessionId'` for the catalog. Typed `unknown`
   * because it arrives from remote JSON.
   */
  route?: unknown
}

/**
 * The writes the walk makes, one per rule type, handed in by `EventManager`.
 *
 * They are the picker screens' own `select`s (`useDeliveryDateActions`,
 * `usePaymentMethodActions`), which every `EventManager` already mounts. The
 * walk calls them with `{ silent: true }`, so it writes exactly as the
 * auto-settle does — no toast, the rollback, and the release of a `'failed'`
 * rule when the write lands — without a second `useCheckoutRuleSelection`
 * per rule in every container on every storefront.
 *
 * Optional only so a caller that never walks need not pass them. A rule whose
 * write is missing cannot be written, and settles `'failed'` like any write
 * that did not land; `EventManager` passes both, so that branch is defensive.
 */
export interface UseStartGuidedWalkArgs {
  selectDeliveryDate?: CheckoutRuleSelect
  selectPaymentMethod?: CheckoutRuleSelect
}

/**
 * The path a walk started from `route` returns to, or `null`.
 *
 * **A route, not a path.** The value must be one of `config.routes[].route`
 * verbatim, and only `:slug` and `:sessionId` are substituted, from the store,
 * by the same `resolveCheckoutRuleRoute` that turns `checkoutRuleRoutes`
 * patterns into paths. So an authored `'//evil.com'`, a literal path, an
 * unknown name or `''` is refused rather than navigated to, and a pattern
 * with any other token comes back `null` from the resolver.
 *
 * `ESCAPES_ORIGIN` still runs on the result. A slug carrying `%2f` decodes to
 * `//…` after substitution, which no membership test can see. The final hop
 * back is a `replace` (`confirm()`), which contains that by itself; the test
 * here means it is never written into the store at all.
 */
export const resolveGuidedReturnPath = (
  route: unknown,
  state: RootState,
): string | null => {
  if (typeof route !== 'string' || route === '') {
    return null
  }

  const routes = state?.defaultSlice?.config?.routes
  if (!Array.isArray(routes)) {
    return null
  }
  if (!routes.some((entry) => entry?.route === route)) {
    return null
  }

  const path = resolveCheckoutRuleRoute(
    route,
    routes,
    state.defaultSlice.storeName,
    state.defaultSlice.sessionId,
  )
  if (!path || ESCAPES_ORIGIN.test(path)) {
    return null
  }

  return path
}

const TIMED_OUT = Symbol('timedOut')

/**
 * `work`, or `TIMED_OUT` after `AUTO_SETTLE_DEADLINE_MS` — the hook's budget,
 * one per leg (fetch, then write), for the hook's reason: Apollo's `HttpLink`
 * carries no timeout, and a leg that never settles would leave the rule
 * `'resolving'` or `'writing'` and the cart's footer disabled behind it.
 */
const withDeadline = <T>(work: Promise<T>): Promise<T | typeof TIMED_OUT> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), AUTO_SETTLE_DEADLINE_MS)
  })

  return Promise.race([work, deadline]).finally(() => clearTimeout(timer))
}

type FetchAnswer =
  | { data: ICheckoutRuleValue[]; preselected: string | null }
  | { error: Error }

type ReportArgs = {
  title: string
  error: Error
  extraInfo: Record<string, unknown>
}

const report = ({ title, error, extraInfo }: ReportArgs) => {
  const context = generateDataDogContext({
    title,
    extraInfo: { function: FUNCTION_NAME, ...extraInfo },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(error, context)
}

type SettleFromArgs = {
  ruleType: CheckoutRuleDocumentType
  from: AutoSettleStatus
  to: AutoSettleStatus
}

type FetchAnswerArgs = {
  ruleType: CheckoutRuleDocumentType
  storeName: string
  sessionId: string
}

/** Who navigates when a run finishes — the latest instance that asked. */
interface WalkRequester {
  /**
   * Whether the shopper is still where they asked from: the instance is
   * mounted **and** its router location is the one it asked at. Mounted
   * alone is not enough, because React reuses the instance across two routes
   * that render the same template (see `useStartGuidedWalk`).
   */
  isStillThere: () => boolean
  push: (path: string) => void
  returnPath: string
}

interface WalkRun {
  requester: WalkRequester
}

/**
 * The walk start in flight, per store.
 *
 * **Module-level, keyed on the store, and not a `useRef`.** `templates/Default`
 * mounts one `EventManager` per container, each with its own emitter, so one
 * `emit` reaches one handler — but the action is fired from `runOnLoad`, which
 * `lazyLoader` re-fires on every mount, and a remount of the catalog is a new
 * `EventManager` and a new instance of this hook. A ref would let each
 * instance start its own walk. Keyed on the store rather than a bare variable
 * so it dies with the store and a test's fresh store starts clean.
 */
const walksInFlight = new WeakMap<AppStore, WalkRun>()

/**
 * `templateActions.checkout.startGuidedWalk` (COM-1501): walk the shopper
 * through every checkout rule they still owe, then back to the screen named
 * in `args.route`.
 *
 * COM-1046's walk starts from the summary CTA, which has the rules' statuses
 * because the summary and the cart mount `useCheckoutRuleAutoSettle`. This one
 * starts from the catalog's `runOnLoad`, where nothing has decided — a
 * shopper who never opened the cart has an empty `autoSettle`, and
 * `selectFirstHeldRule` finds nothing in an empty map. So it decides first:
 *
 * 1. For each served rule (`checkoutRuleRoutes[ruleType]` authored) whose
 *    status is `undefined` or `'resolving'`, fetch its options and apply
 *    `decideAutoSettle` — the hook's own decision, shared rather than copied.
 *    `'resolving'` is re-asked, as the hook re-asks it: it is what a cart
 *    left behind when the shopper walked away mid-fetch. Any other status is
 *    the store's answer and is not fetched again.
 * 2. Then, if the instance that asked is still mounted, push the first held
 *    rule, writing `guidedReturnPath` immediately before the push. With
 *    nothing held it does nothing — no navigation, no return path. It never
 *    queues the order; that is the difference from the summary CTA.
 *
 * **The same outcomes as the hook.** A fetch that fails or runs out its
 * deadline settles `'unavailable'`, so the rule does not hold the order and
 * the walk does not visit its picker; on a timeout the options entry is moved
 * to `'error'` too, so it is not left `'loading'`. A write that is rejected or
 * runs out its deadline settles `'failed'`, so the rule stays held and its
 * picker is visited; the shopper's pick there releases it once it lands
 * (`releaseFailedAutoSettle`). The ticket first asked for `'failed'` on a
 * fetch failure too; code review reversed that (COM-1501), because a fetch
 * that failed says nothing about the value and the hook would let the order
 * through. The one departure is that the hook is not mounted here: mounting
 * it on the catalog would fetch on every storefront's catalog that serves a
 * rule, which is a fleet-wide change this action is opt-in instead of.
 *
 * **Every transition is compare-and-set** (`settleFrom`), from the status this
 * run itself wrote. A run can outlive the catalog — the shopper leaves for the
 * cart mid-fetch — and the cart then mounts the hook over the same rule. The
 * hook reads `'resolving'`, re-asks, and decides; when this run's answer
 * lands it finds the status moved and stands down rather than writing a
 * second time.
 *
 * ⚠️ **The status is dispatched before `setAvailable`, and that order is
 * load-bearing in production.** `index.tsx` mounts through the legacy
 * `ReactDOM.render`, so each dispatch made outside a React event renders
 * synchronously. Dispatch `setAvailable` first and a cart hook mounted
 * mid-fetch renders `'resolving'` beside `'loaded'` options; its effect
 * decides, its `advance('writing')` reads the fresh `'writing'` this run is
 * about to write (transient, so not refused) and proceeds, and `select` runs
 * a second time: two `setCheckoutRuleSession` writes. With the status first,
 * every render that sees the options also sees them decided. RTL renders
 * through a concurrent root, which batches the two dispatches, so the main
 * suite cannot see the order at all; the legacy-root cases in
 * `tests/useStartGuidedWalk.independent.test.tsx` ("beside the cart hook,
 * walk answers first" and "beside the cart hook on the legacy root, nothing
 * to write") kill both reorderings, on the write path and the empty answer.
 * Residual, and accepted: if both answers land within one frame, with the
 * hook's effect for the first still pending, both can write. Same value,
 * same session, and only where the action is authored on a screen that also
 * mounts the hook.
 *
 * **One walk start at a time** (`walksInFlight`). A second call while one is
 * in flight starts no evaluation; it takes over as the one that navigates, so
 * a remounted catalog still gets its walk and an unmounted one does not.
 *
 * **Navigates only if the shopper is still where they asked from.** Mounted
 * is not that: `<Routes>` renders the matched route's element in one place
 * with no key of its own (the `key` in `routes/index.tsx` is on the `<Route>`
 * config element, not on what renders), so two routes whose containers use
 * the same template — six `type: 'catalog'` containers in `defaultConfig.ts`
 * — reconcile into the same `BaseContainer`, `Default` and `EventManager`.
 * A walk asked for on one of them would otherwise push from the other. The
 * pathname is read when the walk is asked for and compared when it finishes.
 *
 * **Once per return path per session** (`checkoutSlice.guidedWalkStarts`).
 * See that field for the loop it prevents.
 *
 * ⚠️ **It always finishes at least one microtask after the call**, even when
 * every status is known and nothing is fetched, and that is load-bearing.
 * `runOnLoad` fires from a component's effect; effects run children-first, so
 * a template that mounts `useEndGuidedWalk` clears *after* a descendant's
 * `runOnLoad` in the same commit. Writing the return path synchronously would
 * be undone by that clear. Writing it after an `await` lands after every
 * effect of the commit has run.
 */
export const useStartGuidedWalk = ({
  selectDeliveryDate,
  selectPaymentMethod,
}: UseStartGuidedWalkArgs = {}) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const pushView = usePushView()
  const queryRule = useGetCheckoutRuleQuery()
  const mountedRef = useRef(false)

  /*
   * Kept current on every render rather than in an effect, so a walk that
   * finishes between a navigation's commit and its passive effects still
   * reads where the shopper is now. A ref rather than the render's value,
   * because `EventManager` re-registers its handlers on store changes only,
   * so the `startGuidedWalk` an emit reaches may be a navigation old.
   */
  const { pathname } = useLocation()
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  const [deliveryType, paymentType] = CHECKOUT_RULE_DOCUMENT_TYPES
  const selectByRule: Partial<
    Record<CheckoutRuleDocumentType, CheckoutRuleSelect>
  > = {
    [deliveryType]: selectDeliveryDate,
    [paymentType]: selectPaymentMethod,
  }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const statusOf = (ruleType: CheckoutRuleDocumentType) =>
    selectAutoSettleStatus(ruleType)(store.getState())

  /** Moves a status only from the one this run wrote. */
  const settleFrom = ({ ruleType, from, to }: SettleFromArgs) => {
    if (statusOf(ruleType) !== from) {
      return
    }
    dispatch(setAutoSettleStatus({ ruleType, status: to }))
  }

  /**
   * Never rejects. A rejection and an `error` on a resolved result are the
   * same answer: no options.
   */
  const fetchAnswer = async ({
    ruleType,
    storeName,
    sessionId,
  }: FetchAnswerArgs): Promise<FetchAnswer> => {
    try {
      const { data, error } = await queryRule({
        storefrontName: storeName,
        ruleType,
        sessionUid: sessionId,
      })
      if (error) {
        return { error }
      }

      return {
        data: data?.getCheckoutRule?.data ?? [],
        preselected: data?.getCheckoutRule?.preselected ?? null,
      }
    } catch (exception) {
      return {
        error:
          exception instanceof Error
            ? exception
            : new Error('getCheckoutRule failed'),
      }
    }
  }

  /**
   * Which leg ran out decides the outcome, as in the hook: a fetch leaves the
   * rule `'unavailable'` (it does not hold the order), a write leaves it
   * `'failed'` (its picker is visited).
   */
  const reportDeadline = (
    ruleType: CheckoutRuleDocumentType,
    interrupted: 'resolving' | 'writing',
  ) => {
    const consequence =
      interrupted === 'writing'
        ? 'it is left failed so the walk visits its picker'
        : 'it is left unavailable and does not hold the order'
    report({
      title: 'a checkout rule did not resolve before the guided walk deadline',
      error: new Error(
        `checkout rule ${ruleType} was still ${interrupted} after ${AUTO_SETTLE_DEADLINE_MS}ms; ${consequence}`,
      ),
      extraInfo: {
        ruleType,
        deadlineMs: AUTO_SETTLE_DEADLINE_MS,
        interrupted,
      },
    })
  }

  /**
   * COM-1528: `commerce-checkout_rule_resolved` fires when the walk owns the
   * terminal decision. The cart/summary hook has the same seam, and
   * `fireCheckoutRuleResolved` holds the shared `(sessionId, ruleType)`
   * guard so a session that enters from the catalog never double-fires if
   * the cart/summary hook later mounts over a terminal status.
   */
  const fireResolved = (
    ruleType: CheckoutRuleDocumentType,
    resolution: Parameters<typeof fireCheckoutRuleResolved>[0]['resolution'],
    optionCount: number,
  ) => {
    fireCheckoutRuleResolved({
      ruleType,
      resolution,
      optionCount,
      sessionId: store.getState().defaultSlice.sessionId,
    })
  }

  /** Decides one rule, if this run is the one to decide it. */
  const settleRule = async (ruleType: CheckoutRuleDocumentType) => {
    const { defaultSlice } = store.getState()
    if (!defaultSlice.config?.checkoutRuleRoutes?.[ruleType]) {
      return
    }

    const status = statusOf(ruleType)
    if (status !== undefined && status !== 'resolving') {
      return
    }

    dispatch(setAutoSettleStatus({ ruleType, status: 'resolving' }))
    dispatch(setLoadingState({ ruleType, loadingState: 'loading' }))

    try {
      const answer = await withDeadline(
        fetchAnswer({
          ruleType,
          storeName: defaultSlice.storeName || '',
          sessionId: defaultSlice.sessionId || '',
        }),
      )

      /*
       * Someone else decided while this run waited — the cart's hook, which
       * re-asks a `'resolving'` rule. Stand down, and leave `loadingState`
       * alone too: the owner that moved the status issued its own fetch,
       * which writes `'loading'` over this run's and settles it, so a
       * `'loading'` still there now is theirs, not ours, and resetting it
       * would contradict a fetch still open.
       */
      if (statusOf(ruleType) !== 'resolving') {
        return
      }

      if (answer === TIMED_OUT) {
        /*
         * `'error'` rather than leaving this run's `'loading'` behind: the
         * picker's empty and error states bind `checkout.ui`, which a
         * `'loading'` entry keeps hidden for as long as nothing re-fetches.
         * Before the status, for the order the error branch below uses.
         */
        dispatch(
          setLoadingState({
            ruleType,
            loadingState: 'error',
            error: `getCheckoutRule did not answer within ${AUTO_SETTLE_DEADLINE_MS}ms`,
          }),
        )
        reportDeadline(ruleType, 'resolving')
        settleFrom({ ruleType, from: 'resolving', to: 'unavailable' })
        // COM-1528: a slow fetch is still a terminal `failed` resolution for
        // GA4. Without this fire, catalog-started sessions whose fetch
        // outlived the 8s deadline are absent from the failure funnel — a
        // later cart/summary mount sees the terminal status and no-ops.
        fireResolved(ruleType, 'failed', 0)
        return
      }

      if ('error' in answer) {
        /*
         * `'error'` lands first. On the legacy root a cart hook mounted
         * mid-fetch renders it at once and settles the rule `'unavailable'`
         * itself; `settleFrom` then finds the status moved and writes
         * nothing. Either way the rule ends `'unavailable'`, as the hook's
         * own failed fetch would leave it.
         */
        dispatch(
          setLoadingState({
            ruleType,
            loadingState: 'error',
            error: answer.error.message,
          }),
        )
        report({
          title: 'getCheckoutRule failed while starting a guided walk',
          error: answer.error,
          extraInfo: { ruleType },
        })
        settleFrom({ ruleType, from: 'resolving', to: 'unavailable' })
        fireResolved(ruleType, 'failed', 0)
        return
      }

      const decision = decideAutoSettle({
        ruleType,
        data: answer.data,
        preselected: answer.preselected,
        state: store.getState(),
        functionName: FUNCTION_NAME,
      })
      const available = setAvailable({
        ruleType,
        data: answer.data,
        preselected: answer.preselected,
      })

      if (!('write' in decision)) {
        settleFrom({ ruleType, from: 'resolving', to: decision.status })
        dispatch(available)
        fireResolved(ruleType, decision.resolution, decision.optionCount)
        return
      }

      /*
       * `setAvailable` before `select`, which matches the chosen id against
       * the offered list to decide what to send — on a keyed list a miss
       * refuses the write.
       */
      settleFrom({ ruleType, from: 'resolving', to: 'writing' })
      dispatch(available)

      /*
       * No stand-down check after this await, unlike the fetch's: nothing
       * but this run moves a rule out of `'writing'` (the hook's guard
       * refuses it), and `settleFrom` would refuse the write if it did.
       */
      const select = selectByRule[ruleType]
      const write = select
        ? select(decision.write, { silent: true }).catch(() => false)
        : Promise.resolve(false)
      const written = await withDeadline(write)

      if (written === TIMED_OUT) {
        reportDeadline(ruleType, 'writing')
        settleFrom({ ruleType, from: 'writing', to: 'failed' })
        fireResolved(ruleType, 'failed', decision.optionCount)
        return
      }

      settleFrom({
        ruleType,
        from: 'writing',
        to: written ? decision.terminal : 'failed',
      })
      fireResolved(
        ruleType,
        written ? 'auto_selected' : 'failed',
        decision.optionCount,
      )
    } catch (exception) {
      /*
       * Nothing above is expected to throw. If something does, a status left
       * transient would hold the cart's footer for the session, and `'writing'`
       * would stop the cart's own hook from ever re-asking.
       */
      report({
        title: 'a checkout rule threw while starting a guided walk',
        error:
          exception instanceof Error
            ? exception
            : new Error('startGuidedWalk threw'),
        extraInfo: { ruleType },
      })
      const current = statusOf(ruleType)
      if (current === 'resolving' || current === 'writing') {
        settleFrom({ ruleType, from: current, to: 'failed' })
        fireResolved(ruleType, 'failed', 0)
      }
    }
  }

  /** Pushes the first held rule, if the requester can still take the walk. */
  const finish = ({ isStillThere, push, returnPath }: WalkRequester) => {
    if (!isStillThere()) {
      return
    }

    /*
     * No second look at `guidedWalkStarts` here: only one run is in flight
     * per store, so nothing can record this path between the start's check
     * and this line.
     */
    const state = store.getState()
    const next = selectFirstHeldRule(state)
    if (!next) {
      return
    }

    dispatch(markGuidedWalkStarted(returnPath))
    dispatch(setGuidedReturn(returnPath))
    push(next.path)
  }

  /**
   * Returns the run's promise, which never rejects, for a caller that wants
   * to await it; `runOnLoad` and an emitted action ignore it.
   */
  const startGuidedWalk = (
    args: StartGuidedWalkArgs = {},
  ): Promise<void> | undefined => {
    const state = store.getState()
    const { storeName, sessionId } = state.defaultSlice
    if (!storeName || !sessionId) {
      return undefined
    }

    const returnPath = resolveGuidedReturnPath(args?.route, state)
    if (!returnPath) {
      report({
        title: 'startGuidedWalk names a route the storefront does not author',
        error: new Error(
          `startGuidedWalk was given route ${JSON.stringify(args?.route ?? null)}, which is not one of config.routes; no walk is started`,
        ),
        extraInfo: { route: JSON.stringify(args?.route ?? null) },
      })
      return undefined
    }

    if (state.checkoutSlice.guidedWalkStarts?.includes(returnPath)) {
      return undefined
    }

    const askedFrom = pathnameRef.current
    const requester: WalkRequester = {
      isStillThere: () =>
        mountedRef.current && pathnameRef.current === askedFrom,
      // COM-1528: the first picker reads `useLocation().state?.entryPoint`
      // and defaults to `cart_change`. The catalog-guided walk is a
      // `guided_walk`, same as the summary entry — pass it on history state
      // so the picker's open event is attributed to the right surface. The
      // outer `state` here is react-router's NavigateOptions; the inner is
      // the history-state payload.
      push: (path) =>
        pushView({
          path,
          state: { state: { entryPoint: 'guided_walk' } },
        }),
      returnPath,
    }

    const running = walksInFlight.get(store)
    if (running) {
      running.requester = requester
      return undefined
    }

    const run: WalkRun = { requester }
    walksInFlight.set(store, run)

    return (async () => {
      try {
        await Promise.all(CHECKOUT_RULE_DOCUMENT_TYPES.map(settleRule))
        finish(run.requester)
      } catch (exception) {
        report({
          title: 'startGuidedWalk threw while finishing',
          error:
            exception instanceof Error
              ? exception
              : new Error('startGuidedWalk threw'),
          extraInfo: { returnPath },
        })
      } finally {
        if (walksInFlight.get(store) === run) {
          walksInFlight.delete(store)
        }
      }
    })()
  }

  return { startGuidedWalk }
}

export default useStartGuidedWalk
